/**
 * "How much have I invoiced the last six months?" as up to six monthly bars,
 * each split into paid and still unpaid (docs/intelligent-chat-answers-plan.md,
 * "Build plan for the designed components", wave 1: Chart).
 *
 * 🚨 ONE SOURCE. The sentence above the chart comes from `get_revenue_summary`
 * → `getRevenueSummary` (`src/lib/invoices/revenue-summary.ts`), which returns
 * one total per period. The bars are the SAME arithmetic run once per month:
 * the same rows (same columns, same account and client scope), the same
 * exclusions (demo rows, drafts, cancelled), the same "raised" date
 * (`sent_at ?? created_at`), the same half-open window compared the same way,
 * and the same rounding. So a bar always equals what `get_revenue_summary`
 * would say if asked about that month alone, and the drawn bars add up to the
 * sentence's figure whenever every month in the range is drawn. It is NOT
 * `getRevenueByMonth` (`src/lib/queries/revenue-reports.ts`), a second source
 * with its own definitions, which is how one September once had three
 * different "paying" counts on three tabs.
 *
 * `__tests__/revenue-chart.test.ts` runs the real `getRevenueSummary`
 * against the same rows, month by month, and fails the day the two drift.
 *
 * - **Invoiced** = `getRevenueSummary().invoicedTotal` for the month.
 * - **Still unpaid** = `outstandingFromPeriod`: of what was raised that month,
 *   what is unpaid TODAY (`asOf`), not what was unpaid at the month's end.
 * - **Paid** = invoiced − still unpaid, done here so the component adds nothing.
 *
 * Pure and directive-free: the route, both chat surfaces and the grader import
 * it (anti-pattern #18).
 */

/** One invoice row, exactly the columns `getRevenueSummary` selects. */
export interface RevenueInvoiceRow {
  id: string;
  total: number | string | null;
  paid_amount: number | string | null;
  status: string | null;
  created_at: string;
  sent_at: string | null;
  cancelled_at: string | null;
  is_demo: boolean | null;
  client_id: string | null;
}

export interface RevenueChartMonth {
  /** YYYY-MM, a London calendar month. */
  month: string;
  /** "Sep". Built here, never with `toLocaleDateString` (#41: "Sept" vs "Sep"). */
  label: string;
  /** "September", "October so far (to the 10th)", "August (from the 15th)". */
  name: string;
  /** Pounds, to the penny. */
  invoiced: number;
  unpaid: number;
  paid: number;
  /** Invoices raised in the month (drafts and cancelled ones not counted). */
  count: number;
  /** The month London is in now: drawn hatched and labelled "so far". */
  current: boolean;
  /** A past month the asked range only covers part of. */
  partial: boolean;
  /** Still unpaid is over `UNPAID_HIGH_SHARE` of the month's invoiced. Never on the current month. */
  unpaidHigh: boolean;
}

export interface RevenueChartBlock {
  kind: 'revenue-chart';
  /** Oldest first, at most `REVENUE_CHART_MAX_MONTHS`. */
  months: RevenueChartMonth[];
  /** The month the detail opens on. */
  focusMonth: string;
  /** Earlier months in the asked range that are not drawn. Never hidden silently. */
  hiddenMonths: number;
  /** The question was about one customer (`client_id`), so the bars are theirs only. */
  oneClient: boolean;
  /** London day the figures were read (YYYY-MM-DD): a stored reply is a snapshot. */
  asOf: string;
  /** `/revenue-reports`, or null when this seat cannot open it. */
  revenuePath: string | null;
}

/** The most bars a chat bubble draws. Earlier months are counted, not dropped. */
export const REVENUE_CHART_MAX_MONTHS = 6;

/**
 * Still unpaid is drawn amber when it is MORE than a quarter of what that
 * month invoiced. A share rather than a pound figure, so it means the same to
 * a two-van firm and a forty-engineer one. The current month is never amber:
 * invoices raised this week are not due yet, so a high share is normal.
 */
export const UNPAID_HIGH_SHARE = 0.25;

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** Mirrors `NOT_INVOICED` in revenue-summary.ts (not exported there). */
const NOT_INVOICED = new Set(['draft', 'cancelled']);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function money(v: number | string | null): number {
  return Number(v ?? 0) || 0;
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function nextMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

function ordinal(day: number): string {
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return `${day}th`;
  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
}

/**
 * The executor's conversion, in one place: the model passes an INCLUSIVE last
 * day, the summary works on `[from, toExclusive)`. Null when the dates would be
 * refused by `executeGetRevenueSummary`, so a chart never outlives a refusal.
 */
export function revenueWindow(input: Record<string, unknown>): { from: string; toExclusive: string } | null {
  const from = typeof input.date_from === 'string' ? input.date_from.trim() : '';
  const to = typeof input.date_to === 'string' ? input.date_to.trim() : '';
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to) || to < from) return null;
  return { from, toExclusive: addDays(to, 1) };
}

/** Every calendar month the window touches, oldest first, as YYYY-MM. */
export function monthsInWindow(from: string, toExclusive: string): string[] {
  const last = addDays(toExclusive, -1).slice(0, 7);
  const out: string[] = [];
  for (let m = from.slice(0, 7); m <= last; m = nextMonth(m)) out.push(m);
  return out;
}

/**
 * 🚨 The attach rule, stated once. A chart appears when the asked window
 * touches TWO OR MORE calendar months. One month is one figure, and one figure
 * is a sentence. "How's the business doing?" gets a chart when the model asks
 * for a multi-month range, which is what that question usually becomes.
 */
export function wantsRevenueChart(from: string, toExclusive: string): boolean {
  return monthsInWindow(from, toExclusive).length >= 2;
}

/**
 * One window across a turn's `get_revenue_summary` calls, as the tool's own
 * input, or null.
 *
 * "How's revenue been over the last three months?" is often asked of the tool
 * one month at a time — July, then August, then September — and each call is
 * one month, so the per-call rule above never draws (demo, 10 Oct 2026). Two
 * or more successful calls for the same customer (or all for none) become the
 * span from the earliest first day to the latest last day, and the chart rule
 * then decides as it would for one call that asked for all of it.
 */
export function revenueWindowAcross(
  calls: ReadonlyArray<{ name: string; input: Record<string, unknown>; error?: string }>,
): { date_from: string; date_to: string; client_id?: string } | null {
  const asked = calls.filter(c => c.name === 'get_revenue_summary' && !c.error);
  if (asked.length < 2) return null;
  const clientOf = (input: Record<string, unknown>) =>
    typeof input.client_id === 'string' && input.client_id ? input.client_id : null;
  const client = clientOf(asked[0].input);
  let from: string | null = null;
  let toExclusive: string | null = null;
  for (const call of asked) {
    if (clientOf(call.input) !== client) return null;
    const w = revenueWindow(call.input);
    if (!w) return null;
    if (from === null || w.from < from) from = w.from;
    if (toExclusive === null || w.toExclusive > toExclusive) toExclusive = w.toExclusive;
  }
  if (from === null || toExclusive === null || !wantsRevenueChart(from, toExclusive)) return null;
  return { date_from: from, date_to: addDays(toExclusive, -1), ...(client ? { client_id: client } : {}) };
}

export interface RevenueChartOptions {
  from: string;
  toExclusive: string;
  /** London's date now, YYYY-MM-DD. Passed in so this stays pure. */
  todayIso: string;
  oneClient: boolean;
  revenuePath: string | null;
}

/**
 * The chart, or null when the window touches fewer than two months (after
 * dropping empty months that have not started yet: a bar for November in
 * October would read as "nothing invoiced", which is not a fact yet).
 */
export function revenueChartBlock(rows: readonly RevenueInvoiceRow[], opts: RevenueChartOptions): RevenueChartBlock | null {
  const { from, toExclusive, todayIso } = opts;
  const raisedAt = (i: RevenueInvoiceRow) => i.sent_at ?? i.created_at;
  // The same exclusions, in the same order, as getRevenueSummary's `raised`.
  const counted = rows.filter(i => !i.is_demo && !NOT_INVOICED.has(i.status ?? '') && !i.cancelled_at);

  const all: RevenueChartMonth[] = monthsInWindow(from, toExclusive).map(month => {
    const monthStart = `${month}-01`;
    const nextStart = `${nextMonth(month)}-01`;
    const lo = from > monthStart ? from : monthStart;
    const hi = toExclusive < nextStart ? toExclusive : nextStart;
    const raised = counted.filter(i => { const at = raisedAt(i); return at >= lo && at < hi; });
    const invoiced = round2(raised.reduce((n, i) => n + money(i.total), 0));
    const unpaid = round2(raised.reduce((n, i) => n + Math.max(0, money(i.total) - money(i.paid_amount)), 0));
    const current = monthStart <= todayIso && todayIso < nextStart;
    const partial = !current && (lo !== monthStart || hi !== nextStart);
    const monthIndex = Number(month.slice(5, 7)) - 1;
    let name = MONTH_LONG[monthIndex];
    if (current) {
      const lastDay = addDays(hi, -1);
      const upTo = todayIso < lastDay ? todayIso : lastDay;
      name = `${name} so far (to the ${ordinal(Number(upTo.slice(8, 10)))})`;
    } else if (partial) {
      const bits: string[] = [];
      if (lo !== monthStart) bits.push(`from the ${ordinal(Number(lo.slice(8, 10)))}`);
      if (hi !== nextStart) bits.push(`to the ${ordinal(Number(addDays(hi, -1).slice(8, 10)))}`);
      name = `${name} (${bits.join(' ')})`;
    }
    return {
      month,
      label: MONTH_SHORT[monthIndex],
      name,
      invoiced,
      unpaid,
      paid: round2(invoiced - unpaid),
      count: raised.length,
      current,
      partial,
      unpaidHigh: !current && invoiced > 0 && unpaid > invoiced * UNPAID_HIGH_SHARE,
    };
  });

  // Months that have not begun and hold nothing are not "nothing invoiced".
  while (all.length && `${all[all.length - 1].month}-01` > todayIso && all[all.length - 1].count === 0) all.pop();
  if (all.length < 2) return null;

  const months = all.slice(-REVENUE_CHART_MAX_MONTHS);
  // A year on the name only when the drawn months cross one, so "December" is
  // never ambiguous beside "January".
  if (months[0].month.slice(0, 4) !== months[months.length - 1].month.slice(0, 4)) {
    for (const m of months) {
      const year = m.month.slice(0, 4);
      m.name = m.name.replace(MONTH_LONG[Number(m.month.slice(5, 7)) - 1], `${MONTH_LONG[Number(m.month.slice(5, 7)) - 1]} ${year}`);
    }
  }
  // Open on the latest finished month — the one with a settled answer — or on
  // the last bar when every drawn month is the current one.
  const finished = months.filter(m => !m.current);
  const focusMonth = (finished.length ? finished[finished.length - 1] : months[months.length - 1]).month;

  return {
    kind: 'revenue-chart',
    months,
    focusMonth,
    hiddenMonths: all.length - months.length,
    oneClient: opts.oneClient,
    asOf: todayIso,
    revenuePath: opts.revenuePath,
  };
}

function pounds(n: number): string {
  return `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "10 Oct", for "Unpaid as at …". Fixed month names (#41). */
export function asOfLabel(iso: string): string {
  return `${Number(iso.slice(8, 10))} ${MONTH_SHORT[Number(iso.slice(5, 7)) - 1]}`;
}

/**
 * Appended to the tool RESULT when the chart is attached. Gives the model the
 * per-month figures off the same rows, so a sentence like "September was your
 * best month" is read from the server and cannot disagree with a bar, and
 * tells it not to recite the chart.
 */
export function revenueChartResultNote(block: RevenueChartBlock): string {
  const byMonth = block.months
    .map(m => `${m.name}: ${pounds(m.invoiced)} invoiced, ${pounds(m.unpaid)} still unpaid`)
    .join('; ');
  const hidden = block.hiddenMonths
    ? ` ${block.hiddenMonths} earlier month${block.hiddenMonths === 1 ? ' is' : 's are'} in the totals above but not drawn.`
    : '';
  return `\n\nThese months are on screen as a bar chart of invoiced and still unpaid by month (raised-date basis, same as the totals above): ${byMonth}.${hidden}`
    + ' Do not read every month out; answer what was asked, and name a month only if it stands out.';
}

/**
 * One line for the oversight transcript: counts, totals and states, nothing a
 * customer could be identified by.
 */
export function summariseRevenueChartBlock(block: RevenueChartBlock): string {
  const n = block.months.length;
  const invoiced = round2(block.months.reduce((s, m) => s + m.invoiced, 0));
  const unpaid = round2(block.months.reduce((s, m) => s + m.unpaid, 0));
  const parts = [
    `${n} month${n === 1 ? '' : 's'}`,
    `${pounds(invoiced)} invoiced`,
    `${pounds(unpaid)} still unpaid`,
  ];
  if (block.months.some(m => m.current)) parts.push('current month so far');
  const high = block.months.filter(m => m.unpaidHigh).length;
  if (high) parts.push(`${high} month${high === 1 ? '' : 's'} over a quarter unpaid`);
  if (block.hiddenMonths) parts.push(`${block.hiddenMonths} earlier not drawn`);
  if (block.oneClient) parts.push('one customer');
  return `[revenue-chart: ${parts.join(', ')}]`;
}
